// ✅ src/pages/OrderManagement/ProjectVendorDetails.jsx
// ASPX parity:
// - Same page for Add + Edit
// - Click Supplier Name (link) => loads record into form (edit mode)
// - Add New => clears form (new mode)
// - Checkbox select + Remove => deletes selected (multi delete)
// - Uses contractId from URL or sessionStorage Selcontractid
// - Added localStorage headers in API calls

import React, { useEffect, useMemo, useState } from "react";
import {
  FaFileInvoiceDollar,
  FaCalendarAlt,
  FaBuilding,
  FaSearch,
  FaFilter,
  FaDownload,
  FaTrash,
  FaPlus,
  FaPrint,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBars,
  FaHome,
  FaChartBar,
  FaUsers,
  FaFileAlt,
  FaTasks,
  FaWarehouse,
  FaSave,
  FaUndo,
  FaUserTie,
  FaEnvelope,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API = "http://nissi-india.com/api/vendorDetails";

const toInt = (v, def = 0) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : def;
};

const VendorDetailsComponent = () => {
  const location = useLocation();

  const [isDark, setIsDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // ✅ safe localStorage helper
  const getLS = (keys, fallback = "") => {
    if (typeof window === "undefined") return fallback;

    const keyList = Array.isArray(keys) ? keys : [keys];
    for (const key of keyList) {
      const val = localStorage.getItem(key);
      if (val !== null && val !== undefined && String(val).trim() !== "") {
        return String(val).trim();
      }
    }
    return fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS(["finYearId"]);
  const finYear = getLS(["finYear"]);
  const branchIdLS = getLS(["branchId", "branchid"]);
  const branchTypeLS = getLS(["branchType", "branchtype"]);
  const userIdLS = getLS(["userId", "loginuserid", "loginUserId"]);

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const loginUserId = useMemo(() => toInt(userIdLS, 0), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId || "",
      "x-finyear": finYear || "",
      "x-branchid": String(branchId || 0),
      "x-branchtype": branchType || "",
      "x-userid": String(loginUserId || 0),
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  // ✅ contractId from URL or session
  const contractId = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const fromUrl = qs.get("contractId") || qs.get("contractid") || "";
    const fromSession =
      sessionStorage.getItem("Selcontractid") ||
      sessionStorage.getItem("selcontractid") ||
      sessionStorage.getItem("contractId") ||
      "";
    return toInt(fromUrl || fromSession || 0);
  }, [location.search]);

  useEffect(() => {
    if (contractId) sessionStorage.setItem("Selcontractid", String(contractId));
  }, [contractId]);

  const sidebarItems = [
    { name: "Date/LOA", icon: FaCalendarAlt, path: "/OrderManagement/ProjectEntry" },
    { name: "BOQ/Operative", icon: FaFileInvoiceDollar, path: "/OrderManagement/BOQ" },
    { name: "Program/DPR", icon: FaChartBar, path: "/OrderManagement/DailyProgressMonthlyWise" },
    { name: "Purchase Order", icon: FaFileAlt, path: "/OrderManagement/PurchaseOrderListing" },
    { name: "Rate Approval", icon: FaCheckCircle, path: "/OrderManagement/RateApprove" },
    { name: "Risk/Issues", icon: FaExclamationTriangle, path: "/OrderManagement/RiskAndIssue" },
    { name: "Documentation", icon: FaFileAlt, path: "/OrderManagement/ProjectEntryDocumentation" },
    { name: "Photos", icon: FaBuilding, path: "/OrderManagement/PhotoDocument" },
    { name: "Reconciliation", icon: FaTasks, path: "/OrderManagement/Reconciliation" },
    { name: "Do/Don't's", icon: FaWarehouse, path: "/OrderManagement/Rules" },
    { name: "Vendor Details", icon: FaUsers, path: "/OrderManagement/ProjectVendorDetails", active: true },
  ];

  // =========================
  // Form + Mode
  // =========================
  const emptyForm = {
    supplierId: 0,
    supplierName: "",
    specialization: "",
    phone: "",
    email: "",
    address: "",
  };

  const [formData, setFormData] = useState(emptyForm);
  const [mode, setMode] = useState("N"); // N=new, E=edit

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setMode("N");
  };

  // =========================
  // Grid / Selection
  // =========================
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const allChecked = rows.length > 0 && selectedIds.length === rows.length;

  const toggleSelectAll = (checked) => {
    if (checked) setSelectedIds(rows.map((r) => Number(r.fld_Supplierid)));
    else setSelectedIds([]);
  };

  const toggleSelectOne = (id, checked) => {
    const sid = Number(id);
    setSelectedIds((prev) => {
      if (checked) return Array.from(new Set([...prev, sid]));
      return prev.filter((x) => x !== sid);
    });
  };

  // =========================
  // Load list
  // =========================
  const loadList = async () => {
    if (!contractId) {
      toast.error("contractId missing in URL");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/view?contractId=${contractId}`, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load vendors");
        return;
      }

      const list = res.data?.rows || [];
      setRows(list);

      setSelectedIds((prev) =>
        prev.filter((id) => list.some((r) => Number(r.fld_Supplierid) === id))
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contractId) loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, commonHeaders]);

  // =========================
  // View / Edit
  // =========================
  const onView = async (supplierId) => {
    const sid = Number(supplierId || 0);
    if (!sid) return;

    setLoading(true);
    try {
      const res = await axios.get(`${API}/get?supplierId=${sid}`, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load vendor");
        return;
      }

      const r = res.data?.row || {};
      setFormData({
        supplierId: Number(r.fld_Supplierid || 0),
        supplierName: r.fld_SupplierName || "",
        specialization: r.fld_Specialization || "",
        phone: r.fld_Phone || "",
        email: r.fld_Email || "",
        address: r.fld_Address || "",
      });
      setMode("E");
      toast.info("Edit mode enabled");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Save
  // =========================
  const handleSubmit = async () => {
    if (!contractId) return toast.error("contractId missing");
    if (!String(formData.supplierName || "").trim()) {
      return toast.error("Please enter Supplier Name");
    }

    setLoading(true);
    try {
      const payload = {
        mode,
        contractId,
        supplierId: Number(formData.supplierId || 0),
        supplierName: String(formData.supplierName || "").trim(),
        specialization: String(formData.specialization || "").trim(),
        phone: String(formData.phone || "").trim(),
        email: String(formData.email || "").trim(),
        address: String(formData.address || "").trim(),
      };

      const res = await axios.post(`${API}/save`, payload, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Save failed");
        return;
      }

      toast.success(res.data?.message || "Saved Successfully");
      resetForm();
      await loadList();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    resetForm();
    toast.info("New mode");
  };

  // =========================
  // Delete selected
  // =========================
  const handleDeleteSelected = async () => {
    if (!selectedIds.length) {
      toast.error("Please select any one");
      return;
    }

    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/delete`,
        { ids: selectedIds },
        { headers: commonHeaders }
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      toast.success(res.data?.message || "Removed Successfully");
      setSelectedIds([]);
      resetForm();
      await loadList();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const openPrint = () => {
    if (!contractId) return toast.error("contractId missing");
    window.open(
      `/OrderManagement/ProjectVendorDetailsPrint?contractId=${contractId}`,
      "_blank",
      "width=1200,height=900,scrollbars=yes"
    );
  };

  const uniqSpecializations = useMemo(() => {
    const set = new Set(
      rows
        .map((r) => String(r.fld_Specialization || "").trim())
        .filter((x) => x)
        .map((x) => x.toLowerCase())
    );
    return set.size;
  }, [rows]);

  return (
    <div className={`min-h-screen transition-all duration-300 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
      <ToastContainer position="top-right" autoClose={2200} />

      {/* Top Bar */}
      <div
        className={`sticky top-0 z-50 backdrop-blur-md border-b ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } shadow-lg`}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <FaBars size={20} />
              </button>
              <div className="flex items-center gap-3">
                <FaHome className="text-blue-600 text-xl" />
                <h1 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Project Management System
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "bg-yellow-600 hover:bg-yellow-700 text-white" : "bg-gray-800 hover:bg-gray-900 text-white"
                }`}
              >
                {isDark ? "☀️" : "🌙"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div
          className={`${sidebarOpen ? "w-64" : "w-16"} transition-all duration-300 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          } border-r min-h-screen`}
        >
          <div className="p-4">
            {sidebarItems.map((item, index) => (
              <Link
                to={`${item.path}?contractId=${contractId || ""}`}
                key={index}
                className={`flex items-center gap-3 p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
                  window.location.pathname === item.path
                    ? isDark
                      ? "bg-blue-600 text-white"
                      : "bg-blue-500 text-white"
                    : isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                <item.icon size={18} />
                {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
              </Link>
            ))}
          </div>
        </div>

        {/* Main */}
        <div className="flex-1">
          <div
            className={`transition-all duration-300 ${
              isDark
                ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
                : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
            }`}
          >
            {/* Header */}
            <div
              className={`backdrop-blur-md border-b ${
                isDark ? "bg-gray-900/80 border-gray-700" : "bg-white/80 border-gray-200"
              }`}
            >
              <div className="px-6 py-4">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                  <div>
                    <h1
                      className={`text-2xl lg:text-3xl font-bold flex items-center gap-3 ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      <FaUsers className="text-blue-500" />
                      VENDOR DETAILS
                    </h1>
                    <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      ContractId: <b>{contractId || "-"}</b> • Mode:{" "}
                      <b className={mode === "E" ? "text-amber-600" : "text-emerald-600"}>
                        {mode === "E" ? "EDIT" : "NEW"}
                      </b>
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      disabled={loading}
                      onClick={handleSubmit}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                        loading
                          ? "bg-green-500/60 text-white cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700 text-white"
                      }`}
                    >
                      <FaSave />
                      Submit
                    </button>

                    <button
                      disabled={loading}
                      onClick={handleAddNew}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                        loading
                          ? "bg-blue-500/60 text-white cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700 text-white"
                      }`}
                    >
                      <FaPlus />
                      Add New
                    </button>

                    <button
                      onClick={openPrint}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                    >
                      <FaPrint />
                      Print
                    </button>

                    <button
                      disabled={loading}
                      onClick={loadList}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                        loading
                          ? "bg-slate-500/60 text-white cursor-not-allowed"
                          : "bg-slate-900 hover:bg-slate-800 text-white"
                      }`}
                    >
                      <FaSearch />
                      Refresh
                    </button>

                    <button
                      disabled={loading}
                      onClick={handleDeleteSelected}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                        loading
                          ? "bg-rose-500/60 text-white cursor-not-allowed"
                          : "bg-rose-600 hover:bg-rose-700 text-white"
                      }`}
                    >
                      <FaTrash />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Form */}
              <div
                className={`rounded-2xl shadow-xl border ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <div className="bg-blue-600 text-white px-6 py-3 rounded-t-2xl">
                  <h2 className="text-lg font-bold">VENDOR DETAILS ENTRY</h2>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-6">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Supplier Name*
                        </label>
                        <input
                          type="text"
                          value={formData.supplierName}
                          onChange={(e) => handleInputChange("supplierName", e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Enter supplier/vendor name..."
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Contact No.
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Enter contact number..."
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Address
                        </label>
                        <textarea
                          rows="4"
                          value={formData.address}
                          onChange={(e) => handleInputChange("address", e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Enter complete address..."
                        />
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Specialization
                        </label>
                        <textarea
                          rows="4"
                          value={formData.specialization}
                          onChange={(e) => handleInputChange("specialization", e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Enter vendor specialization..."
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          E Mail
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Enter email address..."
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-4 mt-6">
                    <button
                      disabled={loading}
                      onClick={resetForm}
                      className={`px-6 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 ${
                        isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-600 hover:bg-gray-700 text-white"
                      }`}
                    >
                      <FaUndo />
                      Reset
                    </button>

                    <button
                      disabled={loading}
                      onClick={handleSubmit}
                      className={`px-6 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 ${
                        loading
                          ? "bg-green-500/60 text-white cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700 text-white"
                      }`}
                    >
                      <FaSave />
                      Save Vendor
                    </button>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div
                className={`rounded-2xl shadow-xl border overflow-hidden ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <div className="bg-blue-600 text-white px-6 py-3 flex items-center justify-between">
                  <h2 className="text-lg font-bold">VENDOR RECORDS</h2>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={loading}
                      onClick={loadList}
                      className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg flex items-center gap-2"
                    >
                      <FaSearch />
                      Refresh
                    </button>
                    <button
                      disabled={loading}
                      onClick={handleDeleteSelected}
                      className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg flex items-center gap-2"
                    >
                      <FaTrash />
                      Remove
                    </button>
                  </div>
                </div>

                <div className="p-0">
                  {loading ? (
                    <div className={`p-8 text-center ${isDark ? "text-gray-200" : "text-gray-700"}`}>Loading...</div>
                  ) : rows.length === 0 ? (
                    <div className="bg-blue-100 border border-blue-300 p-8">
                      <div className="text-center">
                        <FaUsers className="mx-auto text-4xl text-blue-600 mb-4" />
                        <h3 className="text-xl font-bold text-blue-800 mb-2">No Records Found!</h3>
                        <p className="text-blue-700">No vendor records have been created yet.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="overflow-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={isDark ? "bg-gray-900/60 text-gray-100" : "bg-gray-100 text-gray-800"}>
                            <th className="p-3 w-[70px] text-center">S.No</th>
                            <th className="p-3 w-[60px] text-center">
                              <input
                                type="checkbox"
                                checked={allChecked}
                                onChange={(e) => toggleSelectAll(e.target.checked)}
                              />
                            </th>
                            <th className="p-3 text-left">Specialization</th>
                            <th className="p-3 text-left">Supplier Name</th>
                            <th className="p-3 text-left">Phone Number</th>
                            <th className="p-3 text-left">E-Mail</th>
                            <th className="p-3 text-left">Address</th>
                          </tr>
                        </thead>

                        <tbody>
                          {rows.map((r, idx) => {
                            const sid = Number(r.fld_Supplierid);
                            const checked = selectedIds.includes(sid);
                            return (
                              <tr
                                key={sid}
                                className={`border-t ${isDark ? "border-gray-700 text-gray-100" : "border-gray-200 text-gray-800"}`}
                              >
                                <td className="p-3 text-center">{idx + 1}</td>

                                <td className="p-3 text-center">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => toggleSelectOne(sid, e.target.checked)}
                                  />
                                </td>

                                <td className="p-3">{r.fld_Specialization}</td>

                                <td className="p-3">
                                  <button
                                    type="button"
                                    onClick={() => onView(sid)}
                                    className="text-blue-600 hover:text-blue-800 underline"
                                  >
                                    {r.fld_SupplierName}
                                  </button>
                                </td>

                                <td className="p-3">{r.fld_Phone}</td>
                                <td className="p-3">{r.fld_Email}</td>
                                <td className="p-3">{r.fld_Address}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div
                  className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                    isDark
                      ? "bg-gradient-to-br from-blue-900/50 to-blue-800/30 backdrop-blur-md border-blue-700 text-white"
                      : "bg-gradient-to-br from-blue-500 to-blue-600 text-white border-blue-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium">Total Vendors</p>
                      <p className="text-3xl font-bold">{rows.length}</p>
                    </div>
                    <FaUsers className="text-4xl text-blue-200" />
                  </div>
                </div>

                <div
                  className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                    isDark
                      ? "bg-gradient-to-br from-green-900/50 to-green-800/30 backdrop-blur-md border-green-700 text-white"
                      : "bg-gradient-to-br from-green-500 to-green-600 text-white border-green-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm font-medium">Active Suppliers</p>
                      <p className="text-3xl font-bold">{rows.length}</p>
                    </div>
                    <FaUserTie className="text-4xl text-green-200" />
                  </div>
                </div>

                <div
                  className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                    isDark
                      ? "bg-gradient-to-br from-orange-900/50 to-orange-800/30 backdrop-blur-md border-orange-700 text-white"
                      : "bg-gradient-to-br from-orange-500 to-orange-600 text-white border-orange-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm font-medium">Specializations</p>
                      <p className="text-3xl font-bold">{uniqSpecializations}</p>
                    </div>
                    <FaBuilding className="text-4xl text-orange-200" />
                  </div>
                </div>

                <div
                  className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                    isDark
                      ? "bg-gradient-to-br from-purple-900/50 to-purple-800/30 backdrop-blur-md border-purple-700 text-white"
                      : "bg-gradient-to-br from-purple-500 to-purple-600 text-white border-purple-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm font-medium">Selected</p>
                      <p className="text-3xl font-bold">{selectedIds.length}</p>
                    </div>
                    <FaCheckCircle className="text-4xl text-purple-200" />
                  </div>
                </div>
              </div>

              {/* Quick actions */}
              <div
                className={`p-6 rounded-2xl shadow-xl border ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <h3 className={`text-lg font-bold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>Quick Actions</h3>
                <div className="flex flex-wrap gap-4">
                  <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200">
                    <FaSearch />
                    Search Vendors
                  </button>
                  <button className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200">
                    <FaDownload />
                    Export List
                  </button>
                  <button className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200">
                    <FaEnvelope />
                    Send Emails
                  </button>
                  <button className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200">
                    <FaFilter />
                    Filter by Type
                  </button>
                </div>
              </div>

              {/* Hint */}
              <div
                className={`p-5 rounded-2xl shadow-xl border ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700 text-gray-200" : "bg-white border-gray-200 text-gray-700"
                }`}
              >
                <div className="font-bold mb-1">ASPX flow</div>
                <div className="text-sm">
                  Click <b>Supplier Name</b> to edit. Use <b>Add New</b> to clear.
                  Select checkboxes and press <b>Remove</b> to delete.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDetailsComponent;